import React, { FC, useState } from 'react'

import Select from 'react-select'
import { Loader2Icon } from 'lucide-react'

import { ScoreCardOption } from '../../../components/ScoreCardOption'

import { cn } from '../../../../../../lib/utils'
import useToasterHelper from '../../../../../../helpers/ToasterHelper'

import {
  IJudgeOption,
  IScoreCardDiscipline,
} from '../../../../../../models/event-fees/event-fees.interface'
import {
  IUpdateFeesKeys,
  IUpdateRegistrationByDayName,
  RegistrationFeesTypeEx,
} from '../../ManageClinicNOtherOperationsTab'
import { ISelectedIds } from '../JudgingTable/JudgingTable'

import {
  disciplineOptions,
  getJudgeValues,
  getScoreCardsValues,
} from '../../data/judging-tab-utils'

import { MESSAGES_CONST } from '../../../../../../const/messages-const'

interface IBatchJudgingValues {
  discipline: IScoreCardDiscipline
  scoreCard: string
  judges: string[]
}

interface JudgingBatchProps {
  scoreCardHunterJumperOptions: IJudgeOption[]
  getScoreCardsByUser: () => void
  judgesOptions: IJudgeOption[] | null
  selectedIds: ISelectedIds[]
  setSelectedIds: (state: ISelectedIds[]) => void
  isBatchJudging: boolean
  setIsBatchJudging: (state: boolean) => void
  updateFeesKeys: IUpdateFeesKeys
  updateRegistrationByDayName: (state: IUpdateRegistrationByDayName) => Promise<void>
  newRegistrationFees?: RegistrationFeesTypeEx[]
}

export const JudgingBatch: FC<JudgingBatchProps> = ({
  getScoreCardsByUser,
  scoreCardHunterJumperOptions,
  judgesOptions,
  selectedIds,
  setSelectedIds,
  isBatchJudging,
  setIsBatchJudging,
  updateFeesKeys,
  updateRegistrationByDayName,
  newRegistrationFees,
}) => {
  const toastFunctions = useToasterHelper()

  const [batchJudgingValues, setBatchJudgingValues] = useState<IBatchJudgingValues>({
    discipline: null,
    scoreCard: '',
    judges: [],
  })
  const [loading, setLoading] = useState(false)

  const onSaveBatchJudging = async () => {
    try {
      setLoading(true)
      const { judges, discipline, scoreCard } = batchJudgingValues
      const payload: {
        judges?: string[]
        scoreCard?: string
        discipline?: string | null
      } = {}

      if (judges?.[0]) payload.judges = judges
      if (scoreCard) payload.scoreCard = scoreCard || ''
      if (discipline) payload.discipline = discipline || null

      if (Object.keys(payload).length) await updateFeesKeys(payload, selectedIds)

      await updateRegistrationByDayName({
        feeUuid: selectedIds.map((item) => item.uuid),
        sheetId: batchJudgingValues.scoreCard || '',
        discipline: batchJudgingValues.discipline || null,
        judgesValues: [],
      })
      setIsBatchJudging(false)
      setBatchJudgingValues({ discipline: null, scoreCard: '', judges: [] })
      setSelectedIds([])
    } catch (error) {
      console.error(error, 'error')
      toastFunctions.error({
        message: MESSAGES_CONST.SOMETHING_WENT_WRONG,
      })
    } finally {
      setLoading(false)
    }
  }

  return isBatchJudging ? (
    <div className="min-h-[40px] mb-4 flex justify-end w-full flex-wrap items-center gap-3">
      <div className="w-full min-w-[180px] sm:max-w-[180px]">
        <Select
          isDisabled={loading}
          className="text-sm w-full transition-opacity hover:opacity-70"
          isClearable={false}
          isSearchable={true}
          placeholder="Select Discipline"
          value={getJudgeValues(batchJudgingValues.discipline || null, disciplineOptions)}
          options={disciplineOptions}
          isOptionDisabled={(option: IJudgeOption) => option.disable}
          onChange={(newValue) => {
            if (newValue)
              setBatchJudgingValues({
                ...batchJudgingValues,
                discipline: newValue.value as IScoreCardDiscipline,
              })
          }}
          menuPlacement="auto"
          menuPortalTarget={document.body}
        />
      </div>
      <div className="w-full sm:w-[25%] min-w-[260px]">
        <Select
          isDisabled={loading}
          className="text-sm w-full transition-opacity hover:opacity-70"
          isClearable={false}
          isSearchable={true}
          placeholder="Select score card"
          onMenuOpen={getScoreCardsByUser}
          value={getJudgeValues(
            batchJudgingValues.scoreCard || '',
            getScoreCardsValues({
              discipline: batchJudgingValues.discipline,
              scoreCardHunterJumperOptions,
            })
          )}
          options={getScoreCardsValues({
            discipline: batchJudgingValues.discipline,
            scoreCardHunterJumperOptions,
          })}
          components={{
            Option: (props) => <ScoreCardOption {...props} />,
          }}
          onChange={(newValue) => {
            if (newValue)
              setBatchJudgingValues({
                ...batchJudgingValues,
                scoreCard: newValue.value,
              })
          }}
          menuPlacement="auto"
          menuPortalTarget={document.body}
        />
      </div>
      <div className="w-full min-w-[260px] sm:max-w-[260px]">
        <Select
          isDisabled={loading}
          className="text-sm w-full transition-opacity hover:opacity-70"
          isClearable={false}
          isSearchable={false}
          isMulti={true}
          placeholder="Select judges"
          value={
            batchJudgingValues.judges?.[0] && judgesOptions?.[0]
              ? batchJudgingValues.judges.map((judge) => getJudgeValues(judge, judgesOptions))
              : []
          }
          options={judgesOptions ?? []}
          onChange={(values) => {
            if (values)
              setBatchJudgingValues({
                ...batchJudgingValues,
                judges: values.map((item) => item?.value || ''),
              })
          }}
          menuPlacement="auto"
          menuPortalTarget={document.body}
        />
      </div>
      <div className="text-sm ml-5 flex items-center space-x-2 divide-x divide-SeabiscuitDark200ThemeColor/50 divide-solid leading-none">
        <button
          disabled={loading}
          className="disabled:opacity-50 disabled:pointer-events-none underline text-SeabiscuitDark200ThemeColor/50 hover:text-SeabiscuitDark200ThemeColor transition-all"
          onClick={() => {
            setIsBatchJudging(false)
            setBatchJudgingValues({ discipline: null, scoreCard: '', judges: [] })
            setSelectedIds([])
          }}
        >
          Cancel
        </button>
        <button
          disabled={loading}
          className="disabled:opacity-50 disabled:pointer-events-none underline pl-2 text-SeabiscuitDark200ThemeColor/50 hover:text-SeabiscuitDark200ThemeColor transition-all"
          onClick={() => {
            if (newRegistrationFees?.length === selectedIds.length) {
              setSelectedIds([])
            } else {
              setSelectedIds(
                newRegistrationFees?.map((fee) => ({ uuid: fee.uuid, parentId: fee.parentId })) ||
                  []
              )
            }
          }}
        >
          {newRegistrationFees?.length === selectedIds.length ? ' Deselect All' : ' Select All'}
        </button>
        <button
          disabled={loading}
          className={cn(
            'flex items-center gap-1 disabled:opacity-50 disabled:pointer-events-none underline pl-2  transition-all',
            batchJudgingValues.discipline ||
              batchJudgingValues.scoreCard ||
              batchJudgingValues.judges?.[0]
              ? 'text-SeabiscuitMainThemeColor hover:text-SeabiscuitMainThemeColorDark'
              : 'text-SeabiscuitDark200ThemeColor/50 hover:text-SeabiscuitDark200ThemeColor'
          )}
          onClick={onSaveBatchJudging}
        >
          {loading && <Loader2Icon className="animate-spin w-4" />}
          Save Changes
        </button>
      </div>
    </div>
  ) : (
    <div className="text-sm min-h-[40px] mb-4 flex items-center justify-end space-x-2 divide-x divide-SeabiscuitDark200ThemeColor/50 divide-solid underline text-SeabiscuitDark200ThemeColor/50 leading-none">
      <button
        className="hover:text-SeabiscuitDark200ThemeColor transition-all"
        onClick={() => {
          setIsBatchJudging(true)
          setSelectedIds([])
        }}
      >
        Batch Assign
      </button>
    </div>
  )
}
